import { useCallback } from 'react';

import { entityKeyVisibleSelectionRefs } from 'modules/common/viewer/dxfViewer/session/mappingModel';
import type {
  MappingIssueEntityRow,
  MappingIssueGroup,
} from 'modules/common/viewer/dxfViewer/session/mappingValidation';
import type { PanelSelectionMode } from 'modules/common/viewer/dxfViewer/session/selectionModifiers';
import type {
  DrawingModel,
  DxfViewerSession,
} from 'modules/common/viewer/dxfViewer/session/types';
import { useReadableStore } from 'modules/common/viewer/dxfViewer/session/useSessionState';

export const useIssueGroupSelection = (
  session: DxfViewerSession,
  model: DrawingModel,
) => {
  const hiddenLayers = useReadableStore(session.hiddenLayers);

  const refsFor = useCallback(
    (keys: readonly string[]) =>
      entityKeyVisibleSelectionRefs(keys, model, hiddenLayers),
    [model, hiddenLayers],
  );

  const onSelectGroup = useCallback(
    (group: MappingIssueGroup, mode: PanelSelectionMode) => {
      session.selection.merge(
        refsFor(group.entities.map((entity) => entity.key)),
        mode,
      );
    },
    [session, refsFor],
  );

  const onHoverGroup = useCallback(
    (group: MappingIssueGroup | null) => {
      session.selection.setHovered(
        group ? refsFor(group.entities.map((entity) => entity.key)) : [],
      );
    },
    [session, refsFor],
  );

  const onSelectEntity = useCallback(
    (entity: MappingIssueEntityRow, mode: PanelSelectionMode) => {
      session.selection.merge(refsFor([entity.key]), mode);
    },
    [session, refsFor],
  );

  const onHoverEntity = useCallback(
    (entity: MappingIssueEntityRow | null) => {
      session.selection.setHovered(entity ? refsFor([entity.key]) : []);
    },
    [session, refsFor],
  );

  return { onSelectGroup, onHoverGroup, onSelectEntity, onHoverEntity };
};
